import { useMemo } from "react";
import { constructCurrency } from "common/currency";
import { useRakebackBalance } from "@/hooks/firestore/user/useRakebackBalance";
import { useTokens } from "@/hooks/firestore/user/useTokens";
import {
  getPerkConfig,
  type PerkType,
} from "@/features/perks/components/PerkIcon";
import { useHasPermission } from "@/features/auth/hooks/usePermission";
import { useIsRakebackEnabled } from "@/features/perks/hooks/useIsRakebackEnabled";

export type NavPerk = {
  type: PerkType;
  name: string;
  badge?: string;
  pill?: string;
  count: number;
};

const useTokenNavPerks = (): NavPerk[] => {
  const { tokens } = useTokens(true);

  return useMemo(
    () =>
      Object.entries(tokens).flatMap(([type, group]) => {
        const config = getPerkConfig(type as PerkType);
        if (!config) return [];
        const count = group.tokens.reduce(
          (acc, token) => acc + token.allocations.length,
          0,
        );
        return [
          {
            type: type as PerkType,
            name: group.name,
            badge: String(count),
            count,
          },
        ];
      }),
    [tokens],
  );
};

const useRakebackNavPerks = (): NavPerk[] => {
  const { rakeback, isLoading } = useRakebackBalance();
  const { isEnabled: isRakebackEnabled, loading } = useIsRakebackEnabled();
  const hasRakebackPermission = useHasPermission("viewCasinoPromotion");

  const canShowRakeback =
    !loading && isRakebackEnabled && hasRakebackPermission;

  return useMemo(() => {
    if (!canShowRakeback || isLoading || !rakeback || rakeback.balance <= 0) {
      return [];
    }

    return [
      {
        type: "RAKEBACK" as const,
        name: "Rakeback",
        pill: constructCurrency(rakeback.balance, {
          currencyType: rakeback.currency,
        }),
        count: 1,
      },
    ];
  }, [rakeback, isLoading, canShowRakeback]);
};

const useNavPerks = () => {
  const tokenPerks = useTokenNavPerks();
  const rakebackPerks = useRakebackNavPerks();

  const perks = useMemo(
    () => [...tokenPerks, ...rakebackPerks],
    [tokenPerks, rakebackPerks],
  );

  const totalCount = useMemo(
    () => perks.reduce((sum, perk) => sum + perk.count, 0),
    [perks],
  );

  return { perks, totalCount };
};

export { useNavPerks };
